import ResourceManager from '@/Management/ResourceManager';

declare let moment: moment.MomentStatic;

export function dateToTicks(date: Date): number {
    let ticks = (((date.getTime() * 10000) + 621355968000000000) - (date.getTimezoneOffset() * 600000000));
    return ticks;
}

export function timeStampToString(dotnetTicks: any): string {
    return ticksToString(dotnetTicks);
}

export function ticksToString(dotnetTicks: number): string {
    // convert the ticks into something javascript understands
    let ticksSinceEpoch = dotnetTicks - 621355968000000000;
    let millisecondsSinceEpoch = ticksSinceEpoch / 10000;

    // output the result in something the human understands
    let tickDate = new Date(millisecondsSinceEpoch);

    return moment(tickDate.toISOString(), 'YYYY-MM-DDTHH:mm:ss').format('YYYY-MM-DDTHH:mm:ss');
}

export function parseDotNetDateString(dateString: string): Date {
    return parseDotNetDateTimeString(dateString);
}

export function parseDotNetDateTimeString(dateString: string): Date {
    if (!dateString || !_.isString(dateString)) {
        return null;
    }

    let limitedString = dateString.substring(0, 19);
    return moment(limitedString + 'Z', 'YYYY-MM-DDTHH:mm:ss').toDate();
}

export function isDotNetDatetimeString(dateString: string): boolean {
    if (!dateString || !_.isString(dateString)) {
        return false;
    }

    let limitedString = dateString.substring(0, 19);
    return moment(limitedString, 'YYYY-MM-DDTHH:mm:ss', true).isValid();
}

export function toString(date: Date, showTime?: boolean, showDate?: boolean): string {
    let language = ResourceManager.getInstance().getLanguage();
    let format = '';

    if (showTime == null && showDate == null) {
        format = 'L LTS';
    }
    else {
        if (showDate == null || showDate === true) {
			format += language.datePattern;
			if (showTime === true) {
				format += ' ';
			}
        }

        if (showTime == null || showTime === true) {
            format += language.timePattern;
        }

        format = format.replace(/dd/g, 'DD');
        format = format.replace(/d/g, 'DD');
        format = format.replace(/yyyy/g, 'YYYY');
        format = format.replace(/tt/g, 'A');
    }

    if (!format) {
        return '';
    }

    let oldLocale = moment.locale();
    moment.locale(language.cultureInfoName);
    let result = moment(date).format(format);
    moment.locale(oldLocale);
    return result;
}

export function toDotNetDateString(date: Date): string {
    if (date == null) {
        return null;
    }

    return [date.getFullYear().toString().padStart(4, '0'), padLeft(date.getMonth() + 1, 2, '0'), padLeft(date.getDate(), 2, '0')].join('-') + 'T00:00:00';
}

export function toDotNetDateTimeString(date: Date): string {
    if (date == null) {
        return null;
    }

    return [date.getFullYear().toString().padStart(4, '0'), padLeft(date.getMonth() + 1, 2, '0'), padLeft(date.getDate(), 2, '0')].join('-') + 'T' + [padLeft(date.getHours(), 2, '0'), padLeft(date.getMinutes(), 2, '0'), padLeft(date.getSeconds(), 2, '0')].join(':');
}

export function now(): Date {
    return moment().toDate();
}

export function center(dates: Array<Date>): Date {
    let range = (moment as any).range(dates); // TODO: 'moment-range' Types
    let center = moment(range.center()).toDate();

    return center;
}

export function padLeft(number: number, digits: number, string: string): string {
    return new Array(digits - String(number).length + 1).join(string || '0') + number;
}

export function createDateFilter(fieldName: string, date: Date, operator: string): string {
    if (date == null) {
        return '';
    }

    let dimensions = ['year', 'month', 'day'];
    let field = 'this.' + fieldName;

    return createCompareDateByOperatorExpression(field, date, operator, dimensions);
}

export function createTimeFilter(fieldName: string, date: Date, operator: string): string {
    if (date == null) {
        return '';
    }

    let dimensions = ['hour', 'minute', 'second'];
    let field = 'this.' + fieldName;

    return createCompareDateByOperatorExpression(field, date, operator, dimensions);
}

export function createDateAndTimeFilter(fieldName: string, date: Date, operator: string): string {
    if (date == null) {
        return '';
    }

    let dimensions = ['year', 'month', 'day', 'hour', 'minute', 'second'];
    let field = 'this.' + fieldName;

    return createCompareDateByOperatorExpression(field, date, operator, dimensions);
}

export function createCompareDateByOperatorExpression(field, date, operator, dimensions) {
    let expressionByOperator = {
        '<': createBeforeDateExpression(field, date, dimensions),

        '>': createAfterDateExpression(field, date, dimensions),

        '<=': createBeforeDateExpression(field, date, dimensions) + ' OR ' +
            createDateEqualsExpression(field, date, dimensions),

        '>=': createAfterDateExpression(field, date, dimensions) + ' OR ' +
            createDateEqualsExpression(field, date, dimensions),

        '=': createDateEqualsExpression(field, date, dimensions),

        '!=': 'not' + wrapExpression(createDateEqualsExpression(field, date, dimensions))
    };

    let expression = expressionByOperator[operator];
    let wrappedExpression = wrapExpression(expression);
    return wrappedExpression;
}

export function createBeforeDateExpression(field, date, dimensions) {
    return createBeforeAfterDateExpression(field, date, dimensions, '<');
}

export function createAfterDateExpression(field, date, dimensions) {
    return createBeforeAfterDateExpression(field, date, dimensions, '>');
}

export function createDateEqualsExpression(field, date, dimensions) {
    let dimensionValues = splitDateByDimensions(date);

    let query = dimensions.map((dimension) => {
        let value = dimensionValues[dimension];
        return createCompareFieldByDateDimensionExpression(dimension, field, '=', value);
    }).join(' AND ');

    return wrapExpression(query);
}

export function createBeforeAfterDateExpression(field, date, dimensions, operator) {
    let dimensionsCopy = dimensions.slice(0);
    let dimensionValues = splitDateByDimensions(date);

    let queries = [];
    while (dimensionsCopy.length > 1) {
        let smallestDimension = dimensionsCopy.pop();
        let smallestDimensionValue = dimensionValues[smallestDimension];

        let equalsQuery = createDateEqualsExpression(field, date, dimensionsCopy);
        let beforeQuery = createCompareFieldByDateDimensionExpression(smallestDimension, field, operator, smallestDimensionValue);

        queries.push(equalsQuery + ' AND ' + beforeQuery);
    }

    let lastDimension = dimensionsCopy[0];
    let lastDimensionValue = dimensionValues[lastDimension];
    queries.push(createCompareFieldByDateDimensionExpression(lastDimension, field, operator, lastDimensionValue));
    queries.reverse();

    let query = queries.map((q) => wrapExpression(q)).join(' OR ');
    return wrapExpression(query);
}

export function createCompareFieldByDateDimensionExpression(dimension, field, operator, value) {
    return dimension + '(' + field + ') ' + operator + ' ' + value;
}

export function wrapExpression(expression) {
    return '(' + expression + ')';
}

export function splitDateByDimensions(date) {
    return {
        'year': year(date),
        'month': month(date),
        'day': day(date),
        'hour': hour(date),
        'minute': minute(date),
        'second': second(date)
    };
}

export function year(date: Date) {
    let year = date.getFullYear();
    return year > 99 ? year : (year + 2000);
}

export function month(date: Date) {
    return date.getMonth() + 1;
}

export function day(date: Date) {
    return date.getDate();
}

export function hour(date: Date) {
    return date.getHours();
}

export function minute(date: Date) {
    return date.getMinutes();
}

export function second(date: Date) {
    return date.getSeconds();
}

export function extractTimeFromDate(date: Date | string): string {
    if (!date)
        return '';

    const parsedDate = moment(date);
    return parsedDate.format('HH:mm');
}
export function extractDateFromDate(date: Date | string): string {
    if (!date)
        return '';

    const parsedDate = moment(date);
    return parsedDate.format('YYYY-MM-DD');
}

export function extractDateTimeFromDate(date: Date | string): string {
    if (!date)
        return '';

    const parsedDate = moment(date);
    return parsedDate.format('YYYY-MM-DDTHH:mm');
}

export default {
    dateToTicks,
    timeStampToString,
    ticksToString,
    parseDotNetDateString,
    parseDotNetDateTimeString,
    isDotNetDatetimeString,
    toString,
    toDotNetDateString,
    toDotNetDateTimeString,
    now,
    center,
    padLeft,
    createDateFilter,
    createTimeFilter,
    createDateAndTimeFilter,
    createCompareDateByOperatorExpression,
    createBeforeDateExpression,
    createAfterDateExpression,
    createDateEqualsExpression,
    createBeforeAfterDateExpression,
    createCompareFieldByDateDimensionExpression,
    wrapExpression,
    splitDateByDimensions,
    year,
    month,
    day,
    hour,
    minute,
    second,
    extractTimeFromDate,
    extractDateFromDate,
    extractDateTimeFromDate
}